import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Project } from '../../types/project.js';
import type { SpecSummary } from '../../types/board.js';
import * as projectApi from '../../services/project-api.js';
import * as specLinkApi from '../../services/spec-link-api.js';
import type { SpecLinkType } from '../../services/spec-link-api.js';

@customElement('cp-link-spec-dialog')
export class CpLinkSpecDialog extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Boolean }) open = false;
  @property({ type: String }) linkType: SpecLinkType = 'goal';
  @property({ type: Number }) linkId = 0;

  @state() private _projects: Project[] = [];
  @state() private _specs: SpecSummary[] = [];
  @state() private _projectId = 0;
  @state() private _specId = '';
  @state() private _loadingProjects = false;
  @state() private _loadingSpecs = false;
  @state() private _saving = false;
  @state() private _error = '';

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('open') && this.open) {
      this._reset();
      void this._loadProjects();
    }
  }

  private _reset(): void {
    this._projectId = 0;
    this._specId = '';
    this._specs = [];
    this._error = '';
    this._saving = false;
  }

  private async _loadProjects(): Promise<void> {
    this._loadingProjects = true;
    try {
      this._projects = await projectApi.getProjects('active');
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Projekte konnten nicht geladen werden';
    } finally {
      this._loadingProjects = false;
    }
  }

  private async _loadSpecs(projectId: number): Promise<void> {
    this._loadingSpecs = true;
    this._specs = [];
    this._specId = '';
    try {
      this._specs = await projectApi.getSpecs(projectId);
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Specs konnten nicht geladen werden';
    } finally {
      this._loadingSpecs = false;
    }
  }

  private _close(): void {
    this.dispatchEvent(new CustomEvent('dialog-closed', { bubbles: true, composed: true }));
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.open) this._close();
  };

  private _onBackdropClick(e: Event): void {
    if (e.target === e.currentTarget) this._close();
  }

  private _onProjectChange(e: Event): void {
    const id = Number((e.target as HTMLSelectElement).value);
    this._projectId = id;
    this._error = '';
    if (id) void this._loadSpecs(id);
    else { this._specs = []; this._specId = ''; }
  }

  private _onSpecChange(e: Event): void {
    this._specId = (e.target as HTMLSelectElement).value;
    this._error = '';
  }

  private async _onSubmit(): Promise<void> {
    if (this._saving || !this._projectId || !this._specId) return;

    this._saving = true;
    this._error = '';

    try {
      await specLinkApi.createLink(this.linkType, this.linkId, this._projectId, this._specId);
      this.dispatchEvent(new CustomEvent('link-created', { bubbles: true, composed: true }));
      this._close();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler';
      if (msg.includes('bereits')) {
        this._error = 'Diese Spec ist bereits verlinkt';
      } else if (msg.includes('not found') || msg.includes('404')) {
        this._error = 'Spec nicht gefunden';
      } else {
        this._error = msg;
      }
    } finally {
      this._saving = false;
    }
  }

  render() {
    if (!this.open) return nothing;

    const canSubmit = this._projectId > 0 && !!this._specId && !this._saving;

    return html`
      <div class="cp-link-spec-dialog__overlay" @click=${this._onBackdropClick}>
        <div class="cp-link-spec-dialog__dialog">
          <div class="cp-link-spec-dialog__header">
            <h2 class="cp-link-spec-dialog__heading">Spec verlinken</h2>
            <button class="cp-link-spec-dialog__close" @click=${this._close}>&times;</button>
          </div>

          <div class="cp-link-spec-dialog__body">
            <div class="cp-link-spec-dialog__field">
              <label class="cp-link-spec-dialog__label">Projekt</label>
              <select
                class="cp-link-spec-dialog__select"
                @change=${this._onProjectChange}
                ?disabled=${this._loadingProjects}
              >
                <option value="0" ?selected=${this._projectId === 0}>
                  ${this._loadingProjects ? 'Lade Projekte...' : '-- Projekt wählen --'}
                </option>
                ${this._projects.map(p => html`
                  <option value=${p.id} ?selected=${this._projectId === p.id}>${p.name}</option>
                `)}
              </select>
            </div>

            <div class="cp-link-spec-dialog__field">
              <label class="cp-link-spec-dialog__label">Spec</label>
              <select
                class="cp-link-spec-dialog__select"
                @change=${this._onSpecChange}
                ?disabled=${!this._projectId || this._loadingSpecs}
              >
                <option value="" ?selected=${!this._specId}>
                  ${this._loadingSpecs
                    ? 'Lade Specs...'
                    : !this._projectId
                      ? '-- Erst Projekt wählen --'
                      : this._specs.length === 0
                        ? 'Keine Specs verfügbar'
                        : '-- Spec wählen --'}
                </option>
                ${this._specs.map(s => html`
                  <option value=${s.id} ?selected=${this._specId === s.id}>${s.name}</option>
                `)}
              </select>
            </div>

            ${this._error ? html`
              <div class="cp-link-spec-dialog__error">${this._error}</div>
            ` : nothing}
          </div>

          <div class="cp-link-spec-dialog__footer">
            <button
              class="cp-link-spec-dialog__btn cp-link-spec-dialog__btn--cancel"
              @click=${this._close}
            >Abbrechen</button>
            <button
              class="cp-link-spec-dialog__btn cp-link-spec-dialog__btn--save"
              ?disabled=${!canSubmit}
              @click=${() => void this._onSubmit()}
            >${this._saving ? 'Verlinken...' : 'Verlinken'}</button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-link-spec-dialog': CpLinkSpecDialog;
  }
}
